import jsPDF from 'jspdf';
import { formatExerciseDisplay, getExerciseType, EXERCISE_TYPES } from '../../../utils/exerciseFormatters';
import { formatRecoveryDisplaySeconds } from '../../../utils/formatRecoveryTime';

/**
 * Genera PDF scheda allenamento in formato griglia (template 2)
 * Basato sull'esempio fornito con layout a griglia 3x3
 */
export const generateWorkoutPDFGrid = async (workout, athlete, gymLogoUrl, gymName = 'FitGym', apiBaseUrl = null) => {
    // Normalizza apiBaseUrl: assicurati che sia sempre un URL assoluto valido
    // Usa la stessa logica di api.js per garantire coerenza
    if (!apiBaseUrl || apiBaseUrl.trim() === '' || 
        apiBaseUrl === '/api' || 
        apiBaseUrl.startsWith('/api/') || 
        apiBaseUrl.startsWith('/') ||
        apiBaseUrl.includes('localhost')) {
        // Se è vuoto, relativo, o localhost, usa REACT_APP_API_BASE_URL o fallback all'IP del server
        apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.113/api';
    }
    
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const contentWidth = pageWidth - (2 * margin);
    
    let yPosition = margin;

    // ========== ESERCIZI IN GRIGLIA 2 COLONNE ==========
    
    const esercizi = workout.esercizi || [];
    const gridCols = 2; // 2 colonne invece di 3
    const gridRows = 5; // 5 righe per pagina (aumentato da 4 per far entrare 10 esercizi)
    const exercisesPerPage = gridCols * gridRows; // 2x5 = 10 esercizi per pagina
    let exerciseIndex = 0;
    
    // Calcola dimensioni griglia
    const cellWidth = (contentWidth - (gridCols - 1) * 2) / gridCols; // 2mm gap tra celle (ridotto da 3mm)
    const cellHeight = 45; // Altezza ridotta da 50mm a 45mm per far entrare 5 righe
    
    // Funzione per renderizzare l'intestazione (riutilizzabile per tutte le pagine)
    const renderHeader = async (doc, yPos) => {
        let headerY = yPos;
        
        // Logo palestra (se presente) - mantiene proporzioni
        if (gymLogoUrl) {
            try {
                const logoImg = await loadLogoWithDimensions(gymLogoUrl);
                if (logoImg && logoImg.width && logoImg.height) {
                    // Calcola dimensioni proporzionali mantenendo aspect ratio
                    const maxWidth = 25; // mm
                    const maxHeight = 25; // mm
                    const aspectRatio = logoImg.width / logoImg.height;
                    
                    let logoWidth, logoHeight;
                    if (aspectRatio > 1) {
                        // Logo orizzontale (larghezza > altezza)
                        logoWidth = Math.min(maxWidth, maxHeight * aspectRatio);
                        logoHeight = logoWidth / aspectRatio;
                    } else {
                        // Logo verticale o quadrato (altezza >= larghezza)
                        logoHeight = Math.min(maxHeight, maxWidth / aspectRatio);
                        logoWidth = logoHeight * aspectRatio;
                    }
                    
                    doc.addImage(logoImg.dataUrl, logoImg.format || 'PNG', margin, headerY, logoWidth, logoHeight);
                }
            } catch (err) {
                // Logo non caricato, continua senza
            }
        }
        
        // Data odierna in alto a destra
        const today = new Date().toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(today, pageWidth - margin, headerY + 5, { align: 'right' });
        
        // Nome palestra
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text(gymName || 'FITNESS GYM', pageWidth / 2, headerY + 10, { align: 'center' });
        
        headerY += 18;
        
        // Info atleta
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text(athlete.name?.toUpperCase() || 'NOME ATLETA', pageWidth / 2, headerY, { align: 'center' });
        
        headerY += 6;
        headerY += 8;
        
        // Nome scheda e descrizione (sopra la linea separatore)
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        const nomeScheda = workout.nome || 'SCHEDA ALLENAMENTO';
        
        // Descrizione (se presente) - non bold
        if (workout.descrizione) {
            // Calcola la larghezza del nome per posizionare la descrizione dopo
            const nomeWidth = doc.getTextWidth(nomeScheda);
            const descrizioneText = ` - ${workout.descrizione}`;
            const descrizioneWidth = doc.getTextWidth(descrizioneText);
            
            // Calcola la posizione X per centrare nome + descrizione insieme
            const totaleWidth = nomeWidth + descrizioneWidth;
            const startX = (pageWidth - totaleWidth) / 2;
            
            // Scrivi il nome (bold)
            doc.setFont('helvetica', 'bold');
            doc.text(nomeScheda, startX, headerY);
            
            // Scrivi la descrizione (normal) subito dopo
            doc.setFont('helvetica', 'normal');
            doc.text(descrizioneText, startX + nomeWidth, headerY);
        } else {
            // Solo nome scheda, centrato
            doc.text(nomeScheda, pageWidth / 2, headerY, { align: 'center' });
        }
        
        headerY += 6;
        
        // Linea separatore
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, headerY, pageWidth - margin, headerY);
        headerY += 5;
        
        return headerY;
    };
    
    for (let page = 0; page < Math.ceil(esercizi.length / exercisesPerPage); page++) {
        if (page > 0) {
            doc.addPage();
            yPosition = margin;
        }
        
        // Renderizza intestazione per ogni pagina
        yPosition = await renderHeader(doc, yPosition);
        
        // Disegna griglia 2 colonne x 4 righe
        for (let row = 0; row < gridRows; row++) {
            for (let col = 0; col < gridCols; col++) {
                if (exerciseIndex >= esercizi.length) break;
                
                try {
                    const esercizio = esercizi[exerciseIndex];
                    
                    // Determina se questo esercizio è collegato al precedente
                    const isLinked = esercizio.linked_to_previous == 1 || esercizio.linked_to_previous === true || esercizio.linked_to_previous === '1';
                    
                    // Trova il gruppo completo (tutti gli esercizi collegati)
                    let gruppo = null;
                    if (isLinked) {
                        // Trova l'inizio del gruppo (il primo esercizio non collegato)
                        let startIdx = exerciseIndex;
                        while (startIdx > 0 && (esercizi[startIdx - 1].linked_to_previous == 1 || esercizi[startIdx - 1].linked_to_previous === true || esercizi[startIdx - 1].linked_to_previous === '1')) {
                            startIdx--;
                        }
                        
                        // Costruisci il gruppo partendo dal primo
                        gruppo = [esercizi[startIdx]];
                        let j = startIdx + 1;
                        while (j < esercizi.length && (esercizi[j].linked_to_previous == 1 || esercizi[j].linked_to_previous === true || esercizi[j].linked_to_previous === '1')) {
                            gruppo.push(esercizi[j]);
                            j++;
                        }
                    } else {
                        // Questo è il primo esercizio del gruppo (o un esercizio normale)
                        gruppo = [esercizio];
                        let j = exerciseIndex + 1;
                        while (j < esercizi.length && (esercizi[j].linked_to_previous == 1 || esercizi[j].linked_to_previous === true || esercizi[j].linked_to_previous === '1')) {
                            gruppo.push(esercizi[j]);
                            j++;
                        }
                    }
                    
                    const xPos = margin + col * (cellWidth + 2);
                    const yPos = yPosition + row * (cellHeight + 2);
                    
                    await renderGridExercise(doc, esercizio, exerciseIndex + 1, xPos, yPos, cellWidth, cellHeight, apiBaseUrl, gruppo, esercizi, exerciseIndex, isLinked);
                    
                    exerciseIndex++;
                } catch (err) {
                    // Continua con il prossimo esercizio invece di fallire completamente
                    exerciseIndex++;
                }
            }
            if (exerciseIndex >= esercizi.length) break;
        }
    }
    
    // ========== FOOTER ==========
    
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(`Pagina ${i} di ${totalPages}`, pageWidth - margin - 20, pageHeight - 5);
    }
    
    return doc;
};

/**
 * Render singolo esercizio nella griglia
 * Layout: immagine a sinistra, info a destra
 */
const renderGridExercise = async (doc, esercizio, index, xPos, yPos, width, height, apiBaseUrl, gruppo = null, allEsercizi = null, currentIndex = null, isLinked = false) => {
    const padding = 2;
    const innerWidth = width - (padding * 2);
    const innerHeight = height - (padding * 2);
    
    // Bordo della cella
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.rect(xPos, yPos, width, height);
    
    const startX = xPos + padding;
    const startY = yPos + padding + 2; // Spazio extra in alto
    
    // Layout a 2 colonne: sinistra immagine (40%), destra testo (60%)
    const imageColWidth = innerWidth * 0.4;
    const textColWidth = innerWidth * 0.6;
    const gap = 2;
    const imageX = startX;
    const textX = startX + imageColWidth + gap;
    
    // ========== COLONNA SINISTRA: Immagine ==========
    let imageY = startY;
    const imageMaxHeight = innerHeight - 2; // Ridotto per compensare lo spazio extra
    const imageMaxWidth = imageColWidth;
    
    if (esercizio.immagine_statica && esercizio.immagine_statica.trim() !== '') {
        let imageUrl = null;
        try {
            // immagine_statica è il nome file salvato in /var/www/html/uploads/images/
            let imageFileName = esercizio.immagine_statica.trim();
            imageUrl = imageFileName;
            
            // Se non è un URL completo, costruisci l'URL usando serve_image.php (stessa logica del template standard)
            if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                // Usa apiBaseUrl direttamente (stessa logica del template standard)
                imageUrl = `${apiBaseUrl}/serve_image.php?filename=${encodeURIComponent(imageFileName)}`;
            }
            
            const img = await loadImageWithDimensions(imageUrl);
            
            if (img && img.width && img.height) {
                const aspectRatio = img.width / img.height;
                let imgWidth = imageMaxWidth;
                let imgHeight = imgWidth / aspectRatio;
                
                // Se supera l'altezza, usa quella come riferimento
                if (imgHeight > imageMaxHeight) {
                    imgHeight = imageMaxHeight;
                    imgWidth = imgHeight * aspectRatio;
                }
                
                // Centra verticalmente
                const imgY = startY + (imageMaxHeight - imgHeight) / 2;
                // Usa il formato corretto (JPG o PNG) per jsPDF
                const imageFormat = img.format === 'image/jpeg' ? 'JPEG' : 'PNG';
                
                doc.addImage(img.dataUrl, imageFormat, imageX, imgY, imgWidth, imgHeight);
            }
        } catch (err) {
            // Continua senza immagine invece di fallire
        }
    }
    
    // ========== COLONNA DESTRA: Informazioni ==========
    let textY = startY + 2; // Spazio extra in alto per il testo
    
    // Numero esercizio, categoria e tipo di gruppo (Superset, Circuit, Giant Set)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    const categoria = esercizio.gruppo_muscolare || 'Generale';
    
    // Determina se mostrare il tipo di gruppo
    // Mostra il tipo se l'esercizio fa parte di un gruppo (sia primo che collegato)
    let typeLabel = null;
    let typeColor = null;
    if (gruppo && gruppo.length > 1) {
        // Usa il set_type del primo esercizio del gruppo o dell'esercizio corrente
        const setType = (gruppo[0].set_type || esercizio.set_type || 'superset').toLowerCase();
        const typeLabels = {
            'superset': 'SUPERSET',
            'circuit': 'CIRCUIT',
            'giant_set': 'GIANT SET',
            'dropset': 'DROPSET',
            'triset': 'TRISET'
        };
        const typeColors = {
            'superset': [147, 51, 234],  // Viola
            'circuit': [239, 68, 68],    // Rosso
            'giant_set': [168, 85, 247], // Viola-500
            'dropset': [249, 115, 22],   // Arancione
            'triset': [234, 179, 8]      // Giallo
        };
        typeLabel = typeLabels[setType] || 'SUPERSET';
        typeColor = typeColors[setType] || [147, 51, 234];
    } else if (esercizio.set_type && esercizio.set_type !== 'normal') {
        // Anche se non è in un gruppo, mostra il tipo se è diverso da 'normal'
        const setType = esercizio.set_type.toLowerCase();
        const typeLabels = {
            'superset': 'SUPERSET',
            'circuit': 'CIRCUIT',
            'giant_set': 'GIANT SET',
            'dropset': 'DROPSET',
            'triset': 'TRISET'
        };
        const typeColors = {
            'superset': [147, 51, 234],  // Viola
            'circuit': [239, 68, 68],    // Rosso
            'giant_set': [168, 85, 247], // Viola-500
            'dropset': [249, 115, 22],   // Arancione
            'triset': [234, 179, 8]      // Giallo
        };
        typeLabel = typeLabels[setType] || null;
        typeColor = typeColors[setType] || [147, 51, 234];
    }
    
    // Costruisci il testo della categoria
    const categoriaText = `${index} - ${categoria}`;
    doc.text(categoriaText, textX, textY);
    
    // Aggiungi il tipo di gruppo con colore specifico sulla stessa riga (per tutti gli esercizi del gruppo)
    if (typeLabel && typeColor) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(typeColor[0], typeColor[1], typeColor[2]);
        const categoriaWidth = doc.getTextWidth(`${categoriaText} - `);
        doc.text(` - ${typeLabel}`, textX + categoriaWidth, textY);
    }
    
    textY += 6.5; // Spazio aumentato
    
    // Nome esercizio
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    const exerciseName = (esercizio.nome || 'Esercizio').toUpperCase();
    const nameLines = doc.splitTextToSize(exerciseName, textColWidth);
    doc.text(nameLines, textX, textY);
    textY += nameLines.length * 5 + 2;
    
    // Gestione esercizi cardio/isometrici/normali
    const exerciseType = getExerciseType(esercizio);
    const isCardio = exerciseType === EXERCISE_TYPES.CARDIO;
    const isIsometric = exerciseType === EXERCISE_TYPES.ISOMETRIC;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    
    if (isCardio) {
        // ESERCIZI CARDIO - Mostra minuti
        const displayValue = formatExerciseDisplay(esercizio.ripetizioni, esercizio);
        doc.text(displayValue, textX, textY);
        textY += 5.5;
        
        // BPM (se presente)
        if (esercizio.bpm && parseInt(esercizio.bpm) > 0) {
            doc.text(`BPM: ${parseInt(esercizio.bpm)}`, textX, textY);
            textY += 5.5;
        }
        
        // Calorie (se presenti)
        if (esercizio.calorie && parseInt(esercizio.calorie) > 0) {
            doc.text(`Calorie: ${parseInt(esercizio.calorie)}`, textX, textY);
            textY += 5.5;
        }
        
        // Recupero
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            doc.text(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`, textX, textY);
            textY += 5.5;
        }
    } else if (isIsometric) {
        // ESERCIZI ISOMETRICI - Mostra secondi
        const serie = parseInt(esercizio.serie) || 1;
        const displayValue = formatExerciseDisplay(esercizio.ripetizioni, esercizio);
        doc.text(`${serie} serie x ${displayValue}`, textX, textY);
        textY += 5.5;
        
        // Recupero
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            doc.text(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`, textX, textY);
            textY += 5.5;
        }
    } else {
        // ESERCIZI NORMALI - Mostra ripetizioni
        const serie = parseInt(esercizio.serie) || 1;
        const displayValue = formatExerciseDisplay(esercizio.ripetizioni, esercizio);
        doc.text(`${serie} serie x ${displayValue}`, textX, textY);
        textY += 5.5;
        
        // Peso (non mostrato per esercizi senza peso)
        const peso = parseFloat(esercizio.peso) || 0;
        if (peso > 0) {
            doc.text(`Peso: ${peso.toFixed(2).replace('.', ',')} kg`, textX, textY);
            textY += 5.5;
        }
        
        // Recupero
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            doc.text(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`, textX, textY);
            textY += 5.5;
        }
    }
    
    // Note trainer (se presenti) - NOTE: e testo sulla stessa riga per risparmiare spazio
    const trainerNote = parseExerciseNote(esercizio.note);
    if (trainerNote && trainerNote.trim() !== '') {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        
        // Calcola spazio disponibile per la nota (larghezza colonna testo - spazio per "Note: ")
        const noteLabelWidth = doc.getTextWidth('Note: ');
        const availableNoteWidth = textColWidth - noteLabelWidth;
        
        // Dividi il testo della nota in righe se necessario
        const noteLines = doc.splitTextToSize(trainerNote, availableNoteWidth);
        
        // Prima riga: "Note: " + testo (sulla stessa riga)
        doc.text('Note:', textX, textY);
        doc.setFont('helvetica', 'italic');
        doc.text(noteLines[0], textX + noteLabelWidth, textY);
        
        // Se ci sono altre righe, continua sulla riga successiva
        if (noteLines.length > 1) {
            for (let i = 1; i < noteLines.length; i++) {
                textY += 4;
                doc.text(noteLines[i], textX + noteLabelWidth, textY);
            }
        }
        
        textY += 4; // Spazio finale dopo le note
    }
};

/**
 * Carica logo con dimensioni (supporta PNG, JPG, SVG)
 * Restituisce oggetto con dataUrl, width, height, format
 */
const loadLogoWithDimensions = (url) => {
    return new Promise(async (resolve, reject) => {
        // Verifica se è SVG
        const isSvg = url.toLowerCase().endsWith('.svg') || url.includes('.svg?') || url.includes('type=svg');
        
        if (isSvg) {
            // Gestione SVG: carica come testo e converte in immagine
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                
                // Crea un'immagine da SVG
                const img = new Image();
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    // Usa dimensioni naturali dell'SVG per mantenere proporzioni
                    canvas.width = img.naturalWidth || img.width || 500;
                    canvas.height = img.naturalHeight || img.height || 500;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL('image/png');
                    URL.revokeObjectURL(svgUrl);
                    resolve({
                        dataUrl: dataUrl,
                        width: canvas.width,
                        height: canvas.height,
                        format: 'PNG'
                    });
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(svgUrl);
                    reject(new Error('Errore caricamento SVG'));
                };
                
                img.src = svgUrl;
            } catch (err) {
                reject(err);
            }
        } else {
            // Gestione PNG/JPG normale - usa loadImageWithDimensions
            loadImageWithDimensions(url)
                .then(result => resolve(result))
                .catch(err => reject(err));
        }
    });
};

/**
 * Converti URL immagine in base64 (supporta PNG, JPG, SVG)
 * @deprecated Usare loadLogoWithDimensions per mantenere proporzioni
 */
const urlToBase64 = (url) => {
    return new Promise(async (resolve, reject) => {
        // Verifica se è SVG
        const isSvg = url.toLowerCase().endsWith('.svg') || url.includes('.svg?') || url.includes('type=svg');
        
        if (isSvg) {
            // Gestione SVG: carica come testo e converte in immagine
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                
                // Crea un'immagine da SVG
                const img = new Image();
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    // Usa dimensioni standard per logo (puoi aumentare per qualità migliore)
                    canvas.width = 500; // 500px per qualità alta quando ridimensionato a 25mm
                    canvas.height = 500;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL('image/png');
                    URL.revokeObjectURL(svgUrl);
                    resolve(dataUrl);
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(svgUrl);
                    reject(new Error('Errore caricamento SVG'));
                };
                
                img.src = svgUrl;
            } catch (err) {
                reject(err);
            }
        } else {
            // Gestione PNG/JPG normale
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        }
    });
};

/**
 * Carica immagine e restituisce dati con dimensioni
 */
const loadImageWithDimensions = (url) => {
    return new Promise((resolve, reject) => {
        if (!url || url.trim() === '') {
            reject(new Error('URL immagine vuoto'));
            return;
        }
        
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        
        // Timeout per evitare che l'immagine si blocchi
        const timeout = setTimeout(() => {
            reject(new Error('Timeout caricamento immagine'));
        }, 10000); // 10 secondi
        
        img.onload = () => {
            clearTimeout(timeout);
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                // Usa 'image/jpeg' per JPG o 'image/png' per PNG - il canvas converte automaticamente
                // jsPDF accetta entrambi i formati
                const format = img.src.toLowerCase().endsWith('.jpg') || img.src.toLowerCase().endsWith('.jpeg') 
                    ? 'image/jpeg' 
                    : 'image/png';
                const dataUrl = canvas.toDataURL(format);
                resolve({
                    dataUrl: dataUrl,
                    width: img.width,
                    height: img.height,
                    format: format
                });
            } catch (err) {
                reject(err);
            }
        };
        
        img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error(`Errore caricamento immagine: ${url}`));
        };
        
        img.src = url;
    });
};

/**
 * Parsa note esercizio (JSON o stringa legacy) ed estrae testo trainer
 * Supporta backward compatibility con note legacy (stringa semplice)
 */
const parseExerciseNote = (note) => {
    if (!note) return null;
    
    // Se è una stringa che inizia con '{', prova a parsare come JSON
    if (typeof note === 'string' && note.trim().startsWith('{')) {
        try {
            const notesObj = JSON.parse(note);
            // Estrai trainer (priorità) o user/system se trainer non presente
            return notesObj.trainer || notesObj.user || notesObj.system || null;
        } catch (e) {
            // Se non è JSON valido, considera come stringa legacy
            return note.trim();
        }
    }
    
    // Se è già un oggetto (già parsato)
    if (typeof note === 'object' && note !== null) {
        return note.trainer || note.user || note.system || null;
    }
    
    // Note legacy: stringa semplice
    return note.trim() || null;
};

